import { useRef } from "react";
import {
  clearManualDeliveryAttempt,
  deleteManualDelivery,
  openManualDeliveryLabel,
  retryManualDeliveryBooking,
  updateManualDelivery,
} from "util/manualDeliveries";
import { bookingOutcome } from "util/manualDeliveryBooking";
import { rowWindowText, sentence } from "util/manualDeliveryStatus";
import { dayLabel } from "util/manualDeliveryTime";
import { formatCents, scheduleAtConfirmedPrice, storedQuotePrice } from "util/manualShipment";

const noop = () => {};
// error: an Error, or a booking answer's data ({ error }).
const problem = (error) =>
  sentence(error && (error.message || error.error)) || "Something went wrong. Try again.";

// What each row action does. Every change goes to the server and then reloads; the server's answer, not the
// click, decides what the row says. Destructive actions and every booking ask first in the in-page dialog.
// openCopy(row) receives the row for "Copy as new delivery"; the page turns it into the panel's prefill.
export default function useRowActions({
  shopId,
  reload,
  showToast,
  confirm,
  setHighlightId = noop,
  openCopy = noop,
  todayIso,
}) {
  // One action per row at a time: a second click while a dialog or request for that row is open does nothing.
  const busy = useRef(new Set());
  const nameOf = (row) => row.customer_name || row.reference;
  const whenOf = (row) =>
    [row.delivery_date ? dayLabel(row.delivery_date, todayIso) : "", rowWindowText(row)].filter(Boolean).join(", ");

  const printLabel = async (row) => {
    try {
      await openManualDeliveryLabel(shopId, row.id);
      showToast({ title: "Label ready", text: `${row.carrier_name} · ${row.reference}` });
    } catch (error) {
      showToast({ title: "Could not get the label", text: problem(error) });
    }
  };

  const setStatus = async (row, status, title) => {
    try {
      await updateManualDelivery(shopId, row.id, { status, cancel_delivery: false });
      showToast({ title, text: nameOf(row) });
    } catch (error) {
      showToast({ title: "Nothing changed", text: problem(error) });
    }
    await reload();
  };

  // Retry (Not booked) and Book (courier Not sent) book only at a price staff confirm here: the stored price
  // first, and the fresh price again whenever the server answers 409. Without a stored price the first call
  // carries no price, which the server can only answer with a fresh quote, never a booking. Every dialog names
  // the customer and the price, because another row's dialog can replace this one (useConfirm then answers this
  // one no, which books nothing).
  const book = async (row) => {
    const stored = storedQuotePrice(row);
    const askPrice = (message, cents) =>
      confirm({
        title: `Book ${nameOf(row)} with ${row.carrier_name}${Number.isInteger(cents) ? ` for ${formatCents(cents)}` : ""}?`,
        body: message,
        action: "Book",
        tone: "primary",
        cancelLabel: "Not now",
      });
    let outcome;
    try {
      outcome = await scheduleAtConfirmedPrice(
        stored ? stored.priceCents : null,
        (cents) => retryManualDeliveryBooking(shopId, row.id, cents),
        askPrice,
        row.quote && row.quote.raw
      );
    } catch (error) {
      outcome = { cancelled: false, result: null };
    }
    await reload();
    if (outcome.cancelled) {
      showToast({ title: "Not booked", text: `Nothing was booked with ${row.carrier_name}.` });
      return;
    }
    // A 401 stops at sign-in, before anything reaches the carrier, so there is nothing to check there.
    if (outcome.result && outcome.result.status === 401) {
      showToast({ title: "Not booked", text: problem(outcome.result.data) });
      return;
    }
    const result = bookingOutcome(outcome.result);
    if (result.kind === "success") {
      const cents = result.row && Number.isInteger(result.row.price_cents) ? result.row.price_cents : null;
      setHighlightId(row.id);
      showToast({
        title: `Booked with ${row.carrier_name}${cents !== null ? ` · ${formatCents(cents)}` : ""}`,
        text: `${nameOf(row)} · ${whenOf(row)}`,
        action: row.labels ? { label: "Print label", run: () => printLabel(row) } : null,
      });
    } else if (result.kind === "priceChanged") {
      showToast({ title: "The price changed again", text: "Nothing was booked. Try again to see the new price." });
    } else if (result.kind === "refused") {
      showToast({ title: `${row.carrier_name} refused the booking`, text: sentence(result.message) });
    } else {
      showToast({
        title: `We can't tell if ${row.carrier_name} booked this`,
        text: `Check ${row.carrier_name} for ${row.reference} before trying again.`,
      });
    }
  };

  const check = (row) => {
    if (row.dashboard_url) {
      window.open(row.dashboard_url, "_blank", "noopener,noreferrer");
      showToast({ title: `Opening the ${row.carrier_name} dashboard`, text: `Search for ${row.reference}.` });
    } else {
      showToast({
        title: `Check ${row.carrier_name}`,
        text: `Search for ${row.reference} in the ${row.carrier_name} dashboard.`,
      });
    }
  };

  const cancel = async (row) => {
    const atCarrier = !row.own_driver && !!row.courier_reference;
    const ok = await confirm({
      title: "Cancel this delivery?",
      body: atCarrier
        ? `We ask ${row.carrier_name} to cancel first. If they don’t confirm, nothing changes here and we’ll tell you why.`
        : `${nameOf(row)}'s delivery will move to Cancelled.`,
      action: "Cancel delivery",
    });
    if (!ok) return;
    try {
      await updateManualDelivery(shopId, row.id, { status: "cancelled", cancel_delivery: true });
      showToast({
        title: "Delivery cancelled",
        text: atCarrier ? `${row.carrier_name} confirmed the cancellation.` : nameOf(row),
      });
    } catch (error) {
      showToast({ title: "Not cancelled", text: problem(error) });
    }
    await reload();
  };

  const clear = async (row) => {
    const ok = await confirm({
      title: "Clear the stuck booking?",
      body: `Only do this after checking the ${row.carrier_name} dashboard and confirming no booking exists for ${row.reference}. Clearing by mistake can double book.`,
      action: "I checked, clear it",
    });
    if (!ok) return;
    try {
      await clearManualDeliveryAttempt(shopId, row.id);
      showToast({ title: "Booking attempt cleared", text: "You can book it again." });
    } catch (error) {
      // The API layer turns the manager/admin guard's bare 401 into its own message.
      showToast({ title: "Not cleared", text: problem(error) });
    }
    await reload();
  };

  const remove = async (row) => {
    const ok = await confirm({
      title: "Delete this delivery?",
      body: `This removes ${nameOf(row)}'s delivery from the list for good.`,
      action: "Delete",
    });
    if (!ok) return;
    try {
      await deleteManualDelivery(shopId, row.id);
      showToast({ title: "Delivery deleted", text: nameOf(row) });
    } catch (error) {
      // 403 { error } for anyone but an admin, 422 while the carrier may hold a booking.
      showToast({ title: "Not deleted", text: problem(error) });
    }
    await reload();
  };

  const perform = (row, action) => {
    switch (action) {
      case "send_out":
      case "mark_out":
        return setStatus(row, "delivering", "Marked out for delivery");
      case "delivered":
        return setStatus(row, "delivered", "Marked delivered");
      case "not_sent":
        return setStatus(row, "pending", "Moved back to not sent");
      case "book":
        return book(row);
      case "label":
        return printLabel(row);
      case "check":
        return check(row);
      case "copy":
        return openCopy(row);
      case "cancel":
        return cancel(row);
      case "clear":
        return clear(row);
      case "delete":
        return remove(row);
      default:
        return undefined;
    }
  };

  const run = async (row, action) => {
    if (busy.current.has(row.id)) return;
    busy.current.add(row.id);
    try {
      await perform(row, action);
    } finally {
      busy.current.delete(row.id);
    }
  };

  return { run, printLabel };
}
